import { supabase } from "@/integrations/supabase/client";
import { 
  HolidayOverride, 
  DutyRosterValidationResult 
} from '@/types';

/**
 * Service for managing holiday override operations
 * Updated to work with proactive database trigger system
 */
export class HolidayOverrideService {
  /**
   * Get existing holiday overrides for all groups in current year
   * Note: Holiday collisions are now handled proactively by database triggers
   * This method retrieves existing overrides created by the trigger system
   */
  static async fetchHolidayOverrides(
    groupId: string,
    monthYear: string
  ): Promise<any[]> {
    try {
      const currentYear = new Date().getFullYear();
      
      const { data: overrides, error } = await supabase
        .from('holiday_override')
        .select(`
          *,
          holidays:collision_with (
            id,
            name,
            date
          ),
          emp_groups:group_id (
            id,
            group_name,
            company_id
          )
        `)
        .eq('is_active', true)
        .like('month_year', `${currentYear}-%`)
        .order('org_off_day');

      if (error) {
        throw new Error(`Failed to fetch holiday overrides: ${error.message}`);
      }

      return overrides || [];
    } catch (error) {
      console.error('Error fetching holiday overrides:', error);
      throw error;
    }
  }

  /**
   * Get holiday overrides for all groups in a company
   */
  static async fetchCompanyHolidayOverrides(
    companyId: string,
    monthYear: string
  ): Promise<HolidayOverride[]> {
    try {
      const { data: overrides, error } = await supabase
        .from('holiday_override')
        .select(`
          *,
          holidays:collision_with (
            id,
            name,
            date
          ),
          emp_groups:group_id (
            id,
            group_name,
            company_id
          )
        `)
        .eq('month_year', monthYear)
        .eq('is_active', true)
        .eq('emp_groups.company_id', companyId)
        .order('org_off_day');

      if (error) {
        throw new Error(`Failed to fetch company holiday overrides: ${error.message}`);
      }

      return overrides || [];
    } catch (error) {
      console.error('Error fetching company holiday overrides:', error);
      throw error;
    }
  }

  /**
   * Delete a holiday override
   * Note: This deactivates the override rather than deleting it
   */
  static async deleteHolidayOverride(overrideId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('holiday_override')
        .update({ is_active: false })
        .eq('id', overrideId);

      if (error) {
        throw new Error(`Failed to delete holiday override: ${error.message}`);
      }
    } catch (error) {
      console.error('Error deleting holiday override:', error);
      throw error;
    }
  }

  /**
   * Create a manual holiday override
   * Note: This should rarely be needed as triggers handle most cases
   */
  static async createHolidayOverride(
    groupId: string,
    originalDate: string,
    shiftedDate: string,
    holidayId: string,
    monthYear: string
  ): Promise<HolidayOverride> {
    try {
      const { data: override, error } = await supabase
        .from('holiday_override')
        .insert({
          group_id: groupId,
          org_off_day: originalDate,
          off_day_shifted_to: shiftedDate,
          collision_with: holidayId,
          month_year: monthYear,
          is_active: true
        })
        .select()
        .single();

      if (error) {
        throw new Error(`Failed to create holiday override: ${error.message}`);
      }

      return override;
    } catch (error) {
      console.error('Error creating holiday override:', error);
      throw error;
    }
  }

  /**
   * Get holiday override statistics for a company
   */
  static async getHolidayOverrideStats(
    companyId: string,
    monthYear: string
  ): Promise<{
    totalOverrides: number;
    activeOverrides: number;
    groupsAffected: number;
  }> {
    try {
      const { data: overrides, error } = await supabase
        .from('holiday_override')
        .select(`
          id,
          is_active,
          emp_groups:group_id (
            id,
            company_id
          )
        `)
        .eq('month_year', monthYear)
        .eq('emp_groups.company_id', companyId);

      if (error) {
        throw new Error(`Failed to fetch holiday override stats: ${error.message}`);
      }

      const totalOverrides = overrides?.length || 0;
      const activeOverrides = overrides?.filter(o => o.is_active).length || 0;
      const groupsAffected = new Set(overrides?.map(o => (o.emp_groups as any)?.id).filter(Boolean)).size;

      return {
        totalOverrides,
        activeOverrides,
        groupsAffected
      };
    } catch (error) {
      console.error('Error fetching holiday override stats:', error);
      throw error;
    }
  }
}